import classnames from 'classnames';
import * as React from 'react';
import '../../utils/index.js';
import { Icon } from '../icons/index.js';
import '../router/index.js';
import '../spinner/index.js';
import '../visually_hidden/index.js';
import { hideLabel, disabled, fullWidth, loading, size, kind, button, spinnerContainer, spinner, label, inner, justify, icon } from './button.css.js';
import { useIsMounted } from '../../utils/is_mounted.js';
import { Spinner } from '../spinner/spinner.js';
import { useRouter } from '../router/router.js';
import { VisuallyHidden } from '../visually_hidden/visually_hidden.js';

/**
 * Primary component for all kinds of actions.
 */
const Button = React.forwardRef(function Button({ children, className, kind: kind$1 = 'neutral', size: size$1 = 'medium', type = 'button', loading: loading$1 = false, disabled: disabled$1 = false, iconStart, iconEnd, hideLabel: hideLabel$1 = false, fullWidth: fullWidth$1 = false, justifyContent, onClick, ...rest }, ref) {
    const isMounted = useIsMounted();
    const [waiting, setWaiting] = React.useState(false);
    const isLoading = loading$1 || waiting;
    const isDisabled = disabled$1 || isLoading;
    const handleClick = React.useCallback(async (event) => {
        event.stopPropagation();
        if (isDisabled) {
            event.preventDefault();
            return;
        }
        if (onClick === undefined) {
            return;
        }
        setWaiting(true);
        try {
            const value = onClick(event);
            if (value instanceof Promise) {
                await Promise.resolve(value);
            }
        }
        finally {
            if (isMounted()) {
                setWaiting(false);
            }
        }
    }, [onClick, isDisabled, isMounted]);
    return (React.createElement("button", { ...rest, ref: ref, className: classnames({
            [button]: true,
            [kind[kind$1]]: true,
            [size[size$1]]: true,
            [loading]: isLoading,
            [fullWidth]: fullWidth$1,
            [disabled]: disabled$1,
            [hideLabel]: hideLabel$1,
        }, className), onClick: handleClick, "aria-disabled": isDisabled ? true : undefined, type: type },
        React.createElement("span", { className: spinnerContainer }, isLoading && React.createElement(Spinner, { size: spinnerSizeMap[size$1], className: spinner })),
        React.createElement(ButtonContent, { iconStart: iconStart, iconEnd: iconEnd, hideLabel: hideLabel$1, justifyContent: justifyContent }, children)));
});
/**
 * Primary component for all kinds of actions, for cases when the action should render an anchor (`<a>`).
 */
const ButtonLink = React.forwardRef(function ButtonLink({ children, className, kind: kind$1 = 'neutral', size: size$1 = 'medium', justifyContent, iconStart, iconEnd, hideLabel: hideLabel$1 = false, fullWidth: fullWidth$1 = false, onClick: propOnClick, ...rest }, ref) {
    const handleClick = React.useCallback((event) => {
        event.stopPropagation();
        propOnClick?.(event);
    }, [propOnClick]);
    const { onClick } = useRouter({ onClick: handleClick });
    return (React.createElement("a", { ...rest, ref: ref, className: classnames({
            [button]: true,
            [kind[kind$1]]: true,
            [size[size$1]]: true,
            [fullWidth]: fullWidth$1,
            [hideLabel]: hideLabel$1,
        }, className), onClick: onClick },
        React.createElement(ButtonContent, { iconStart: iconStart, iconEnd: iconEnd, hideLabel: hideLabel$1, justifyContent: justifyContent }, children)));
});
const ButtonContent = ({ children, iconStart, iconEnd, hideLabel, justifyContent = 'center', }) => {
    const label$1 = React.createElement("span", { className: label }, children);
    return (React.createElement("span", { className: classnames(inner, justify[justifyContent]) },
        iconStart && React.createElement(Icon, { name: iconStart, className: icon }),
        hideLabel ? React.createElement(VisuallyHidden, null, label$1) : label$1,
        iconEnd && React.createElement(Icon, { name: iconEnd, className: icon })));
};
const spinnerSizeMap = {
    xsmall: 'small',
    small: 'small',
    medium: 'medium',
};

export { Button, ButtonLink };
